import { useTranslation } from 'react-i18next';
import * as vp from 'vopay';

export type ItemProps = {
  label: string;
  value: string;
};

type TicketCreationRows = {
  label: string;
  id: vp.CreateTicketId;
  require: boolean;
  visible: (ctx: vp.SubmitTicketRequest) => boolean;
  getSelectOptions?: (ctx: vp.SubmitTicketRequest) => { [key: string]: string };
  type: 'input' | 'select' | 'textarea' | 'inputfile' | 'multiSelector';
  preset: keyof vp.TextFieldPresets | undefined;
  getMultiSelectOptions?: (value: string) => ItemProps[];
  getMultiSelectValues?: (value: string) => ItemProps[];
  searchCallback?: (searchTerm: string) => void;
};

interface TicketCategory {
  ID: string;
  Category: string;
}

interface SubCategory {
  SupportTicketCatagories_ID: string;
  SubCategory: string;
}

export const useBuildTicketCreationRows = (
  accounts: { [key: string]: string },
  TicketCategory: TicketCategory[] = [],
  TicketSubCategories: SubCategory[] = [],
  Users: {},
  onAccountSearch?: (searchTerm: string) => void,
): TicketCreationRows[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'Tickets.Create' });
  return [
    {
      label: t('categoryLabel'),
      id: vp.CreateTicketId.TicketCategory,
      require: true,
      preset: 'text',
      getSelectOptions: () => TicketCategory.reduce((acc, value) => ({ ...acc, [value.ID]: value.Category }), {}),
      type: 'select',
      visible: () => true,
    },
    {
      label: t('subCategoryLabel'),
      id: vp.CreateTicketId.TicketSubCategory,
      require: true,
      preset: 'text',
      getSelectOptions: (ctx: vp.SubmitTicketRequest) => {
        const result = TicketSubCategories.filter((el) => el.SupportTicketCatagories_ID === ctx.TicketCategory).reduce(
          (acc, value) => ({ ...acc, [value.SubCategory]: value.SubCategory }),
          {},
        );
        return result;
      },
      type: 'select',
      visible: () => true,
    },
    {
      label: t('transactionIDLabel'),
      id: vp.CreateTicketId.TransactionID,
      getSelectOptions: () => ({}),
      require: false,
      preset: 'text',
      type: 'input',
      visible: (ctx: vp.SubmitTicketRequest) => ctx.TicketCategory === '2',
    },
    {
      label: t('accountNameLabel'),
      id: vp.CreateTicketId.AccountName,
      getSelectOptions: () => accounts,
      require: false,
      preset: 'text',
      type: 'select',
      visible: () => true,
      searchCallback: onAccountSearch,
    },
    {
      label: t('subjectLabel'),
      id: vp.CreateTicketId.TicketSubject,
      getSelectOptions: () => ({}),
      require: true,
      preset: 'text',
      type: 'input',
      visible: () => true,
    },
    {
      label: t('descriptionLabel'),
      id: vp.CreateTicketId.TicketContent,
      getSelectOptions: () => ({}),
      require: true,
      preset: 'text',
      type: 'textarea',
      visible: () => true,
    },
    {
      label: t('ccEmailsLabel'),
      id: vp.CreateTicketId.CcEmails,
      require: false,
      preset: 'text',
      type: 'multiSelector',
      visible: () => true,
      getMultiSelectValues: (CcEmails: string) => {
        const users = Users;
        if (!CcEmails) return [];
        let test: string | string[] = [];
        if (CcEmails) {
          test = CcEmails.split('|');
        }
        const outputArray: ItemProps[] = Object.entries(users)
          .filter(([key]) => test.includes(key))
          .map(([key, value]) => {
            const labelValue = value as string;

            return {
              label: labelValue || '',
              value: key || '',
            };
          });

        return outputArray || [];
      },
      getMultiSelectOptions: (searchTerm: string) => {
        const users = Users;
        const outputArray: ItemProps[] = Object.entries(users)
          .filter(([, value]) => {
            const labelValue = value as string;
            return labelValue.toLowerCase().includes(searchTerm.toLowerCase());
          })
          .map(([key, value]) => {
            const labelValue = value as string;

            return {
              label: labelValue || '',
              value: key || '',
            };
          });
        return outputArray || [];
      },
    },
    {
      label: t('attachmentLabel'),
      id: vp.CreateTicketId.TicketAttachment,
      getSelectOptions: () => ({}),
      require: true,
      preset: 'text',
      type: 'inputfile',
      visible: () => true,
    },
  ];
};
